import { useImageViewerLogic } from 'features/knowledgeDocumentViewer/components/viewer/image/hooks/useImageViewerLogic'
import { RolodexPagePreloader } from 'features/knowledgeDocumentViewer/components/viewer/image/hooks/useRolodexPagePreloader'
import type { SearchHighlightData } from 'features/knowledgeDocumentViewer/components/viewer/search/SearchHighlightOverlay'
import { CrossReferencePageMarker } from 'features/knowledgeDocumentViewer/components/viewer/shared/CrossReferencePageMarker'
import { DocumentMinimap } from 'features/knowledgeDocumentViewer/components/viewer/shared/DocumentMinimap'
import type {
  ContinuousViewportState,
  DocumentViewerViewportState,
} from 'features/knowledgeDocumentViewer/components/viewer/shared/documentViewerViewport'
import { useAliasTooltip } from 'features/knowledgeDocumentViewer/components/viewer/shared/hooks/useAliasTooltip'
import { useBoxHighlight } from 'features/knowledgeDocumentViewer/components/viewer/shared/hooks/useBoxHighlight'
import { useBoxStyles } from 'features/knowledgeDocumentViewer/components/viewer/shared/hooks/useBoxStyles'
import { useLiveValueChips } from 'features/knowledgeDocumentViewer/components/viewer/shared/hooks/useLiveValueChips'
import { useMinimapPreference } from 'features/knowledgeDocumentViewer/components/viewer/shared/hooks/useMinimapPreference'
import {
  useViewerKeyDown,
  useZoomHotkeys,
} from 'features/knowledgeDocumentViewer/components/viewer/shared/hooks/useViewerHotkeys'
import { PageImageStatus } from 'features/knowledgeDocumentViewer/components/viewer/shared/PageImageStatus'
import { PhotoCountBadgeLayer } from 'features/knowledgeDocumentViewer/components/viewer/shared/PhotoCountBadgeLayer'
import { preferInstantViewerMotion } from 'features/knowledgeDocumentViewer/components/viewer/shared/preferInstantViewerMotion'
import {
  CONTEXT_FRAMING,
  framedRegionScale,
} from 'features/knowledgeDocumentViewer/components/viewer/shared/regionFraming'
import { SubjectTimeSeriesTooltip } from 'features/knowledgeDocumentViewer/components/viewer/shared/SubjectTimeSeriesTooltip'
import {
  type DocumentViewerHandle,
  type FrameTarget,
  type TransformState,
  type ViewerCommonProps,
} from 'features/knowledgeDocumentViewer/components/viewer/shared/types'
import { ViewerOverlaySvg } from 'features/knowledgeDocumentViewer/components/viewer/shared/ViewerOverlaySvg'
import { ZoomControls } from 'features/knowledgeDocumentViewer/components/viewer/shared/ZoomControls'
import { useCrossReferenceContext } from 'features/knowledgeDocumentViewer/contexts/CrossReferenceContext'
import { useDocumentTabDataContext } from 'features/knowledgeDocumentViewer/contexts/DocumentTabDataContext'
import { useKnowledgeDocumentUXContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentUXContext'
import { EMPTY_PAGE_GRAPH_SOURCES } from 'features/knowledgeDocumentViewer/graph/canvasTypes'
import { projectNodeOverlays } from 'features/knowledgeDocumentViewer/graph/graphGeometry'
import { usePublishGraphNodeSelection } from 'features/knowledgeDocumentViewer/graph/GraphNodeSelectionContext'
import { GraphStatus } from 'features/knowledgeDocumentViewer/graph/GraphStatus'
import { GraphTooltip } from 'features/knowledgeDocumentViewer/graph/GraphTooltip'
import { getNodeFocusTarget } from 'features/knowledgeDocumentViewer/graph/nodeFocusTarget'
import { useGraphInstrumentation } from 'features/knowledgeDocumentViewer/graph/useGraphInstrumentation'
import { useGraphOverlaySession } from 'features/knowledgeDocumentViewer/graph/useGraphOverlaySession'
import { useNodeFocusRequest } from 'features/knowledgeDocumentViewer/graph/useNodeFocusRequest'
import { usePageFlowAnimation } from 'features/knowledgeDocumentViewer/graph/usePageFlowAnimation'
import { usePageIsolation } from 'features/knowledgeDocumentViewer/graph/usePageIsolation'
import { DocumentViewerPage } from 'features/knowledgeDocumentViewer/types'
import { crossReferenceRegion } from 'features/knowledgeDocumentViewer/utils/crossReferenceContext'
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import {
  ContinuousPageImage,
  type PageImageDisplayFailure,
} from './ContinuousPageImage'
import {
  PageListWrapper,
  PageOverlayContainer,
  PageSlot,
  PageSpacer,
  ScrollContainer,
} from './continuousScrollStyles'
import { useContinuousPinchZoom } from './hooks/useContinuousPinchZoom'
import { useContinuousScrollSync } from './hooks/useContinuousScrollSync'
import { MAX_ZOOM, useContinuousZoom } from './hooks/useContinuousZoom'
import { useCurrentPageRolodexOverride } from './hooks/useCurrentPageRolodexOverride'
import { useDragToScroll } from './hooks/useDragToScroll'
import { useRafScrollPosSync } from './hooks/useRafScrollPosSync'
import { useVirtualPageWindow } from './hooks/useVirtualPageWindow'
import { buildPageLayoutMetrics } from './pageLayoutMetrics'

const PAGE_GAP = 8
const SCROLL_PADDING = 16

type ContinuousScrollViewerProps = ViewerCommonProps & {
  pageNumber: number
  pdfUrl: string
  scale: number
  totalPages: number
  onPageChange: (page: number) => void
  isActiveTab: boolean
  searchHighlights?: SearchHighlightData
  frameTarget?: FrameTarget
  onFrameApplied?: () => void
  initialViewport?: ContinuousViewportState | null
  onViewportChange?: (viewport: DocumentViewerViewportState) => void
}

export const ContinuousScrollViewer = forwardRef<
  DocumentViewerHandle,
  ContinuousScrollViewerProps
>(function ContinuousScrollViewer(props, ref) {
  const {
    pageNumber,
    pdfUrl,
    scale,
    totalPages,
    onPageChange,
    isActiveTab,
    highlightBoxIds,
    onBoxClick,
    onBoxAuxClick,
    onBoxContextMenu,
    onBoxHover,
    onAliasClick,
    onDocReferenceClick,
    onDocReferenceHover,
    actionMode,
    searchHighlights,
    frameTarget,
    onFrameApplied,
    showTooltips = false,
    pageGraphs = EMPTY_PAGE_GRAPH_SOURCES,
    graphPageNumber = 1,
    onOpenOffPageConnector,
    initialViewport = null,
    onViewportChange,
  } = props

  const crossReference = useCrossReferenceContext()
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const pageListRef = useRef<HTMLDivElement>(null)
  const svgRef = useRef<SVGSVGElement>(null)
  const [pageDimsMap, setPageDimsMap] = useState<
    Map<number, { w: number; h: number }>
  >(() => new Map())
  const [containerSize, setContainerSize] = useState({ w: 0, h: 0 })
  const [failedPage, setFailedPage] = useState<{
    pageNumber: number
    retry: () => void
  } | null>(null)
  const handlePageImageStateChange = useCallback(
    (pageNum: number, failure: PageImageDisplayFailure | null) =>
      setFailedPage((prev) => {
        if (failure) return { pageNumber: pageNum, retry: failure.retry }
        return prev?.pageNumber === pageNum ? null : prev
      }),
    []
  )
  const [rolodexHiddenPages, setRolodexHiddenPages] = useState<
    ReadonlySet<number>
  >(() => new Set())
  const handleRolodexSourceHidden = useCallback(
    (pageNum: number, hidden: boolean) =>
      setRolodexHiddenPages((prev) => {
        if (prev.has(pageNum) === hidden) return prev
        const next = new Set(prev)
        if (hidden) next.add(pageNum)
        else next.delete(pageNum)
        return next
      }),
    []
  )
  const pendingScrollRestoreRef = useRef<ContinuousViewportState | null>(
    initialViewport
  )
  const hasRestoredScrollRef = useRef(false)
  const lastExternalPageRef = useRef<number | null>(null)
  const onViewportChangeRef = useRef(onViewportChange)
  onViewportChangeRef.current = onViewportChange
  // On unmount, DOM scrollTop often reads 0 — persist last known-good instead.
  const latestViewportRef = useRef({
    zoom: initialViewport?.zoom ?? 0,
    scrollTop: initialViewport?.scrollTop ?? 0,
    scrollLeft: initialViewport?.scrollLeft ?? 0,
    isAtMinZoom: initialViewport?.isAtMinZoom ?? true,
  })

  useEffect(() => {
    const el = scrollContainerRef.current
    if (!el) return
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0]
      if (!entry) return
      const next = {
        w: entry.contentRect.width,
        h: entry.contentRect.height,
      }
      setContainerSize((prev) => {
        if (prev.w === next.w && prev.h === next.h) return prev
        return next
      })
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const referenceDims = useMemo<{ w: number; h: number } | null>(() => {
    if (pageDimsMap.size === 0) return null
    return pageDimsMap.get(1) ?? pageDimsMap.values().next().value ?? null
  }, [pageDimsMap])

  const getPageHeightForZoom = useCallback(
    (zoom: number, pageNum: number): number => {
      if (!referenceDims || zoom === 0) return 0
      const width = referenceDims.w * zoom
      const dims = pageDimsMap.get(pageNum)
      if (dims) return width * (dims.h / dims.w)
      return width * (referenceDims.h / referenceDims.w)
    },
    [pageDimsMap, referenceDims]
  )

  const initialZoomForHook = initialViewport?.isAtMinZoom
    ? 0
    : (initialViewport?.zoom ?? 0)

  const {
    effectiveZoom,
    minZoom,
    zoomIn,
    zoomOut,
    resetZoom,
    setZoomLevel,
    snapshotZoomAnchor,
    applyAnchoredZoom,
    isAtMinZoom,
    isAtMaxZoom,
  } = useContinuousZoom(
    scrollContainerRef,
    referenceDims,
    containerSize,
    SCROLL_PADDING,
    PAGE_GAP,
    totalPages,
    getPageHeightForZoom,
    initialZoomForHook
  )

  useDragToScroll(scrollContainerRef)
  useContinuousPinchZoom(
    scrollContainerRef,
    pageListRef,
    snapshotZoomAnchor,
    applyAnchoredZoom,
    minZoom,
    MAX_ZOOM
  )

  const displayWidth = useMemo(() => {
    if (!referenceDims || effectiveZoom === 0) return 0
    return referenceDims.w * effectiveZoom
  }, [referenceDims, effectiveZoom])

  const getPageDisplayHeight = useCallback(
    (pageNum: number): number => getPageHeightForZoom(effectiveZoom, pageNum),
    [effectiveZoom, getPageHeightForZoom]
  )

  const onFrameAppliedRef = useRef(onFrameApplied)
  onFrameAppliedRef.current = onFrameApplied

  const applyFrameScroll = useCallback(
    (target: FrameTarget): boolean => {
      const container = scrollContainerRef.current
      if (!container) return false
      const pageSlotSelector = `[data-page="${target.pageNumber}"]`
      const slot = container.querySelector<HTMLElement>(pageSlotSelector)
      if (!slot || slot.offsetWidth === 0 || slot.offsetHeight === 0) {
        return false
      }
      const { bbox } = target
      const centerX =
        slot.offsetLeft + (bbox.left + bbox.width / 2) * slot.offsetWidth
      const centerY =
        slot.offsetTop + (bbox.top + bbox.height / 2) * slot.offsetHeight
      container.scrollLeft = Math.max(0, centerX - container.clientWidth / 2)
      container.scrollTop = Math.max(0, centerY - container.clientHeight / 2)
      onFrameAppliedRef.current?.()
      return true
    },
    [scrollContainerRef]
  )

  const pendingFrameScrollRef = useRef<FrameTarget | null>(null)
  const lastFramedTargetRef = useRef<FrameTarget | null>(null)

  const suppressInitialScroll = initialViewport !== null

  const { currentPage, loadedRange, handleExternalPageChange } =
    useContinuousScrollSync(
      scrollContainerRef,
      totalPages,
      getPageDisplayHeight,
      PAGE_GAP,
      pageNumber,
      onPageChange,
      suppressInitialScroll
    )

  useEffect(() => {
    // Skip page-top scroll when frame already positioned the viewport.
    if (
      frameTarget &&
      frameTarget.pageNumber === pageNumber &&
      lastFramedTargetRef.current === frameTarget
    ) {
      return
    }
    if (pendingScrollRestoreRef.current && !hasRestoredScrollRef.current) {
      return
    }
    if (lastExternalPageRef.current === pageNumber) {
      return
    }
    if (hasRestoredScrollRef.current && lastExternalPageRef.current === null) {
      lastExternalPageRef.current = pageNumber
      return
    }
    lastExternalPageRef.current = pageNumber
    handleExternalPageChange(pageNumber)
  }, [pageNumber, handleExternalPageChange, frameTarget])

  // Wait for layout before restoring scroll — otherwise the browser clamps to 0.
  useLayoutEffect(() => {
    const pending = pendingScrollRestoreRef.current
    const container = scrollContainerRef.current
    if (
      !pending ||
      hasRestoredScrollRef.current ||
      !container ||
      effectiveZoom === 0 ||
      displayWidth === 0 ||
      getPageDisplayHeight(pageNumber) === 0
    ) {
      return
    }

    const maxScrollTop = Math.max(
      0,
      container.scrollHeight - container.clientHeight
    )
    const maxScrollLeft = Math.max(
      0,
      container.scrollWidth - container.clientWidth
    )
    if (
      pending.scrollTop > maxScrollTop + 1 ||
      pending.scrollLeft > maxScrollLeft + 1
    ) {
      return
    }

    container.scrollTop = pending.scrollTop
    container.scrollLeft = pending.scrollLeft
    hasRestoredScrollRef.current = true
    pendingScrollRestoreRef.current = null
    lastExternalPageRef.current = pageNumber
    latestViewportRef.current = {
      zoom: effectiveZoom,
      scrollTop: pending.scrollTop,
      scrollLeft: pending.scrollLeft,
      isAtMinZoom,
    }
    onViewportChangeRef.current?.({
      kind: 'continuous',
      zoom: effectiveZoom,
      scrollTop: pending.scrollTop,
      scrollLeft: pending.scrollLeft,
      isAtMinZoom,
    })
  }, [
    effectiveZoom,
    displayWidth,
    getPageDisplayHeight,
    pageNumber,
    loadedRange,
    isAtMinZoom,
  ])

  useEffect(() => {
    const container = scrollContainerRef.current
    if (!container || effectiveZoom === 0) return

    const persist = (scrollTop: number, scrollLeft: number) => {
      latestViewportRef.current = {
        zoom: effectiveZoom,
        scrollTop,
        scrollLeft,
        isAtMinZoom,
      }
      onViewportChangeRef.current?.({
        kind: 'continuous',
        zoom: effectiveZoom,
        scrollTop,
        scrollLeft,
        isAtMinZoom,
      })
    }

    const reportFromDom = () => {
      if (pendingScrollRestoreRef.current && !hasRestoredScrollRef.current) {
        return
      }
      const scrollTop = container.scrollTop
      const scrollLeft = container.scrollLeft
      const prev = latestViewportRef.current
      // Ignore teardown scroll events that clamp to 0.
      const contentCollapsed =
        container.scrollHeight <= container.clientHeight + 1 &&
        container.scrollWidth <= container.clientWidth + 1
      const wipedNonZeroOrigin =
        scrollTop === 0 &&
        scrollLeft === 0 &&
        (prev.scrollTop > 1 || prev.scrollLeft > 1) &&
        contentCollapsed
      if (wipedNonZeroOrigin) {
        return
      }
      persist(scrollTop, scrollLeft)
    }

    reportFromDom()
    container.addEventListener('scroll', reportFromDom, { passive: true })
    return () => {
      container.removeEventListener('scroll', reportFromDom)
      // Flush last known-good on unmount — DOM scroll is unreliable after detach.
      const latest = latestViewportRef.current
      onViewportChangeRef.current?.({
        kind: 'continuous',
        zoom: latest.zoom,
        scrollTop: latest.scrollTop,
        scrollLeft: latest.scrollLeft,
        isAtMinZoom: latest.isAtMinZoom,
      })
    }
  }, [effectiveZoom, isAtMinZoom])

  useLayoutEffect(() => {
    const pending = pendingFrameScrollRef.current
    if (!pending) return
    if (applyFrameScroll(pending)) {
      pendingFrameScrollRef.current = null
      if (pending === frameTarget) lastFramedTargetRef.current = pending
    }
  }, [effectiveZoom, displayWidth, loadedRange, applyFrameScroll, frameTarget])

  const frameViewerTarget = useCallback(
    (target: FrameTarget): boolean => {
      const container = scrollContainerRef.current
      if (!container || !referenceDims || effectiveZoom === 0) return false
      const dims = pageDimsMap.get(target.pageNumber)
      if (!dims) return false

      const ideal = framedRegionScale(
        target.bbox,
        { w: referenceDims.w, h: referenceDims.w * (dims.h / dims.w) },
        { w: container.clientWidth, h: container.clientHeight },
        target.framing ?? CONTEXT_FRAMING
      )
      const targetZoom = Math.min(MAX_ZOOM, Math.max(minZoom, ideal))

      if (Math.abs(targetZoom - effectiveZoom) < 0.001) {
        pendingFrameScrollRef.current = target
        if (applyFrameScroll(target)) {
          pendingFrameScrollRef.current = null
          return true
        }
        return false
      }

      pendingFrameScrollRef.current = target
      setZoomLevel(targetZoom)
      return false
    },
    [
      referenceDims,
      effectiveZoom,
      pageDimsMap,
      minZoom,
      setZoomLevel,
      applyFrameScroll,
    ]
  )

  useEffect(() => {
    if (!frameTarget || lastFramedTargetRef.current === frameTarget) return
    if (frameViewerTarget(frameTarget))
      lastFramedTargetRef.current = frameTarget
  }, [frameTarget, frameViewerTarget])

  const handlePageDimensionsKnown = useCallback(
    (pageNum: number, w: number, h: number) => {
      if (w <= 0 || h <= 0) return
      setPageDimsMap((prev) => {
        if (prev.has(pageNum)) return prev
        const next = new Map(prev)
        next.set(pageNum, { w, h })
        return next
      })
    },
    []
  )

  useZoomHotkeys({
    enabled: isActiveTab,
    onResetZoom: resetZoom,
    onZoomIn: zoomIn,
    onZoomOut: zoomOut,
  })

  const handleScrollKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return
    const container = scrollContainerRef.current
    if (!container) return
    e.preventDefault()
    container.scrollBy({
      top: e.key === 'ArrowUp' ? -120 : 120,
      behavior: preferInstantViewerMotion() ? 'auto' : 'smooth',
    })
  }, [])
  useViewerKeyDown(isActiveTab, handleScrollKeyDown)

  const {
    hoverId,
    activeTabId,
    currentSelectedBoxIds,
    updateDocumentHistoryItem,
    handleMouseEnter: ctxMouseEnter,
    handleMouseLeave: ctxMouseLeave,
    photoMode,
    showPhotoCounts,
    togglePhotoCounts,
  } = useKnowledgeDocumentUXContext()
  const [internalHighlightedBoxIds, setInternalHighlightedBoxIds] =
    useBoxHighlight(highlightBoxIds)
  const { fillFor, strokeFor } = useBoxStyles(
    hoverId,
    internalHighlightedBoxIds,
    actionMode
  )
  const { hasTimeSeriesData, documentId } = useDocumentTabDataContext()

  const { currentPageUrl, pdfUrlForFallback, currentPageOverride } =
    useCurrentPageRolodexOverride(
      currentPage,
      pdfUrl,
      rolodexHiddenPages.has(currentPage)
    )
  const { boxes: currentPageBoxes, dims: currentPageDims } =
    useImageViewerLogic(
      pdfUrlForFallback,
      currentPage,
      scale,
      DocumentViewerPage.LEFT_PAGE,
      totalPages,
      isActiveTab,
      currentPageOverride
    )

  const nodeOverlays = useMemo(() => {
    if (currentPage !== pageNumber || !currentPageDims) return []
    return projectNodeOverlays(
      pageGraphs
        .filter((page) => page.pageNumber === graphPageNumber)
        .map(({ graph, pageNumber }) => ({
          graph,
          pageNumber,
          bounds: {
            denormalized_x: 0,
            denormalized_y: 0,
            denormalized_width: currentPageDims.w,
            denormalized_height: currentPageDims.h,
          },
        }))
    )
  }, [currentPage, pageNumber, currentPageDims, pageGraphs, graphPageNumber])
  const instrumentation = useGraphInstrumentation({
    pageGraphs,
    overlays: nodeOverlays,
    documentId,
    isActiveTab,
  })
  const isolation = usePageIsolation({ pageGraphs, isActiveTab })
  const { onStopFocused } = isolation
  const nodeSelection = usePublishGraphNodeSelection(
    pageGraphs,
    instrumentation.datapointsByNodeId
  )
  const flowLayer = usePageFlowAnimation({
    pageGraphs,
    overlays: nodeOverlays,
    isActiveTab,
    stops: isolation.stops,
    highlights: isolation.highlights,
    focus: isolation.focus,
    isolating: isolation.isolating,
  })
  const { chips: liveChips } = useLiveValueChips({
    boxes: currentPageBoxes,
    imageNatural: currentPageDims ?? { w: 0, h: 0 },
  })

  const handleBackgroundClick = useCallback(() => {
    if (currentSelectedBoxIds.length > 0) {
      updateDocumentHistoryItem(activeTabId, { selectedBoxIds: [] })
    }
  }, [activeTabId, currentSelectedBoxIds, updateDocumentHistoryItem])

  const handleBoxMouseEnter = useCallback(
    (_box: React.ComponentProps<typeof ViewerOverlaySvg>['boxes'][number]) => {
      ctxMouseEnter(_box)
      onBoxHover?.(_box)
    },
    [ctxMouseEnter, onBoxHover]
  )

  const handleBoxMouseLeave = useCallback(() => {
    ctxMouseLeave()
    onBoxHover?.(null)
  }, [ctxMouseLeave, onBoxHover])

  const noopSvgMouse = useCallback(() => {}, [])

  const pageLayoutMetrics = useMemo(
    () => buildPageLayoutMetrics(totalPages, getPageDisplayHeight, PAGE_GAP),
    [getPageDisplayHeight, totalPages]
  )

  const virtualPageWindow = useVirtualPageWindow(
    loadedRange,
    totalPages,
    PAGE_GAP,
    pageLayoutMetrics
  )

  const currentPageTop = useMemo(
    () => pageLayoutMetrics.tops[currentPage - 1] ?? 0,
    [currentPage, pageLayoutMetrics]
  )

  const fitComplete = referenceDims !== null && effectiveZoom > 0

  const isMinimapZoomed = minZoom > 0 && !isAtMinZoom
  const { showMinimap, isMinimapEnabled, toggleMinimap } = useMinimapPreference(
    {
      isZoomed: isMinimapZoomed,
      isContentReady:
        currentPageUrl !== undefined && referenceDims !== null && minZoom > 0,
    }
  )

  const scrollPos = useRafScrollPosSync(scrollContainerRef, showMinimap)

  const currentPageNaturalDims = useMemo(
    () => pageDimsMap.get(currentPage) ?? referenceDims,
    [pageDimsMap, currentPage, referenceDims]
  )

  const minimapTransformState = useMemo<TransformState>(() => {
    if (!referenceDims || !scrollContainerRef.current) {
      return { scale: 1, positionX: 0, positionY: 0 }
    }
    const pageScale = displayWidth / referenceDims.w
    return {
      scale: pageScale,
      positionX: -scrollPos.left,
      positionY: -(scrollPos.top - currentPageTop),
    }
  }, [referenceDims, displayWidth, currentPageTop, scrollPos])

  const handleMinimapNavigate = useCallback(
    (posX: number, posY: number) => {
      const container = scrollContainerRef.current
      if (!container || !referenceDims) return
      container.scrollLeft = -posX
      container.scrollTop = currentPageTop + -posY
    },
    [referenceDims, currentPageTop]
  )

  const preloadPages = useMemo(() => {
    const pages: number[] = []
    for (
      let p = Math.max(1, loadedRange.start - 1);
      p <= Math.min(totalPages, loadedRange.end + 1);
      p++
    ) {
      if (p < loadedRange.start || p > loadedRange.end) {
        pages.push(p)
      }
    }
    return pages
  }, [loadedRange, totalPages])

  useImperativeHandle(
    ref,
    () => ({
      zoomToBox: () => {},
      panToRegion: () => {},
      setHighlightedBoxIds: setInternalHighlightedBoxIds,
      resetZoom,
      zoomIn,
      zoomOut,
      setTransform: () => {},
      getViewportInfo: () =>
        scrollContainerRef.current
          ? {
              wrapperWidth: scrollContainerRef.current.clientWidth,
              wrapperHeight: scrollContainerRef.current.clientHeight,
            }
          : null,
      getImageNaturalDimensions: () => referenceDims ?? { w: 0, h: 0 },
    }),
    [referenceDims, resetZoom, zoomIn, zoomOut, setInternalHighlightedBoxIds]
  )

  const { handleAliasHover, aliasTooltipProps } = useAliasTooltip({
    showTooltips,
    containerRef: scrollContainerRef,
  })
  const {
    nodeInspection,
    nodeOverlayLayer,
    suspend: suspendNodeOverlayInspection,
    dismiss: dismissNodeOverlayInspection,
  } = useGraphOverlaySession({
    overlays: nodeOverlays,
    liveValueAppearances: instrumentation.appearances,
    svgRef,
    containerRef: scrollContainerRef,
    onBoxMouseLeave: handleBoxMouseLeave,
    onAliasHover: handleAliasHover,
    onDocReferenceHover,
    onOpenOffPageConnector,
    onSelectFlowNode: isolation.onSelectNode,
    onSelectNode: nodeSelection.selectNode,
    selectedNodeId: nodeSelection.selectedNodeId,
  })
  const focusStop = useCallback(
    (id: string): boolean => {
      const target = getNodeFocusTarget(pageGraphs, id)
      if (!target) return false
      suspendNodeOverlayInspection()
      const framed = frameViewerTarget(target)
      if (framed) onStopFocused(id)
      return framed
    },
    [pageGraphs, suspendNodeOverlayInspection, frameViewerTarget, onStopFocused]
  )
  useNodeFocusRequest({ pageGraphs, isActiveTab, focus: focusStop })
  useEffect(() => {
    suspendNodeOverlayInspection()
  }, [effectiveZoom, suspendNodeOverlayInspection])

  const { aliasHighlights } = useDocumentTabDataContext()
  const hasSearchHighlights =
    searchHighlights !== undefined && searchHighlights.matches.length > 0
  const hasAliasHighlights = aliasHighlights.some(
    (highlight) => highlight.page === pageNumber
  )
  const overlayReady =
    currentPageDims !== null &&
    currentPageDims.w > 0 &&
    (currentPageBoxes.length > 0 ||
      hasSearchHighlights ||
      hasAliasHighlights ||
      nodeOverlays.length > 0) &&
    currentPage === pageNumber

  return (
    <>
      <ScrollContainer ref={scrollContainerRef} $opacity={fitComplete ? 1 : 0}>
        <PageListWrapper
          ref={pageListRef}
          $gap={PAGE_GAP}
          $padding={SCROLL_PADDING}>
          {virtualPageWindow.topSpacerHeight > 0 && (
            <PageSpacer
              $width={displayWidth}
              $height={virtualPageWindow.topSpacerHeight}
            />
          )}
          {virtualPageWindow.pages.map((pageNum) => (
            <PageSlot
              key={pageNum}
              data-page={pageNum}
              onContextMenu={(event) => {
                const bounds = event.currentTarget.getBoundingClientRect()
                if (!bounds.width || !bounds.height) return
                const x = Math.max(
                  0,
                  Math.min(1, (event.clientX - bounds.left) / bounds.width)
                )
                const y = Math.max(
                  0,
                  Math.min(1, (event.clientY - bounds.top) / bounds.height)
                )
                crossReference?.onContextMenu(
                  {
                    selected_text: '',
                    point: [x, y],
                    source_bbox: crossReferenceRegion(x, y),
                    source_page_number: pageNum,
                  },
                  event
                )
              }}
              $width={displayWidth}
              $height={pageLayoutMetrics.heights[pageNum - 1] ?? 0}>
              <ContinuousPageImage
                pageNumber={pageNum}
                pdfUrl={
                  pageNum === currentPage ? pdfUrl || undefined : undefined
                }
                totalPages={totalPages}
                isCurrentPage={pageNum === currentPage && isActiveTab}
                onDimensionsKnown={handlePageDimensionsKnown}
                onRolodexSourceHidden={handleRolodexSourceHidden}
                onDisplayStateChange={handlePageImageStateChange}
              />
              <CrossReferencePageMarker page={pageNum} />
              {pageNum === currentPage && overlayReady && (
                <PageOverlayContainer>
                  <ViewerOverlaySvg
                    showCrossReferenceMarker={false}
                    actionMode={actionMode}
                    nodeOverlayLayer={nodeOverlayLayer}
                    boxes={currentPageBoxes}
                    fillFor={fillFor}
                    hasTimeSeriesData={hasTimeSeriesData}
                    imageNatural={currentPageDims}
                    isDragging={false}
                    isMultipage={false}
                    liveChips={liveChips}
                    onBackgroundClick={handleBackgroundClick}
                    onBoxAuxClick={onBoxAuxClick}
                    onBoxClick={onBoxClick}
                    onBoxContextMenu={onBoxContextMenu}
                    onBoxMouseEnter={handleBoxMouseEnter}
                    onBoxMouseLeave={handleBoxMouseLeave}
                    onAliasClick={onAliasClick}
                    onAliasHover={handleAliasHover}
                    onDocReferenceClick={onDocReferenceClick}
                    onDocReferenceHover={onDocReferenceHover}
                    onMouseDown={noopSvgMouse}
                    onMouseMove={noopSvgMouse}
                    onMouseUp={noopSvgMouse}
                    searchHighlights={searchHighlights}
                    selectionBox={null}
                    strokeFor={strokeFor}
                    svgRef={svgRef}
                    flowLayer={
                      flowLayer && {
                        ...flowLayer,
                        pxPerImagePx: displayWidth / currentPageDims.w,
                      }
                    }
                  />
                  <PhotoCountBadgeLayer
                    imageNatural={currentPageDims}
                    counterZoom={false}
                  />
                </PageOverlayContainer>
              )}
            </PageSlot>
          ))}
          {virtualPageWindow.bottomSpacerHeight > 0 && (
            <PageSpacer
              $width={displayWidth}
              $height={virtualPageWindow.bottomSpacerHeight}
            />
          )}
        </PageListWrapper>
      </ScrollContainer>
      {instrumentation.message && (
        <GraphStatus
          message={instrumentation.message}
          failed
          retry={instrumentation.retry}
        />
      )}
      {failedPage && failedPage.pageNumber === currentPage && (
        <PageImageStatus
          pageNumber={failedPage.pageNumber}
          retry={failedPage.retry}
        />
      )}
      {nodeInspection && (
        <GraphTooltip
          inspection={nodeInspection}
          datapoints={instrumentation.datapointsByNodeId.get(
            nodeInspection.overlay.node.id
          )}
          onDismiss={dismissNodeOverlayInspection}
        />
      )}
      {!nodeInspection && aliasTooltipProps && (
        <SubjectTimeSeriesTooltip {...aliasTooltipProps} />
      )}
      <ZoomControls
        onZoomIn={zoomIn}
        onZoomOut={zoomOut}
        onResetZoom={resetZoom}
        onToggleMinimap={toggleMinimap}
        isMinimapVisible={isMinimapEnabled}
        onTogglePhotoCounts={photoMode ? togglePhotoCounts : undefined}
        arePhotoCountsVisible={showPhotoCounts}
        disabled={!fitComplete}
        zoomOutDisabled={isAtMinZoom}
        zoomInDisabled={isAtMaxZoom}
      />
      {showMinimap && currentPageUrl && currentPageNaturalDims && (
        <DocumentMinimap
          imageUrls={[currentPageUrl]}
          transformState={minimapTransformState}
          imageNaturalDimensions={currentPageNaturalDims}
          wrapperDimensions={containerSize}
          onNavigate={handleMinimapNavigate}
          isMultipage={false}
          wheelTargetRef={scrollContainerRef}
          wheelMode='scroll'
        />
      )}
      {preloadPages.map((page) => (
        <RolodexPagePreloader key={page} page={page} />
      ))}
    </>
  )
})
